import { Badge, Button, Tag, Tooltip } from "@jhool-io/fe-components";
import {
    ClaimActivityType,
    ClaimStatus,
    IClaimActivity,
} from "../types/claims.types";
import {
    displayNameInRightFormat,
    removeEnumUnderscore,
} from "../../../utils/helpers";

export const getStatusTag = (status: ClaimStatus, text?: string) => {
    let bgColor: string;
    let textColor: string;

    switch (status) {
        case ClaimStatus.ACKNOWLEDGED:
            textColor = "#00563E";
            bgColor = "rgba(204, 250, 233, 0.50)";
            break;
        case ClaimStatus.DENIED:
            textColor = "#981F41";
            bgColor = "rgba(251, 199, 198, 0.50)";
            break;
        case ClaimStatus.PAID:
            textColor = "#00563E";
            bgColor = "rgba(204, 250, 233, 0.50)";
            break;
        case ClaimStatus.PENDING:
            textColor = "#634D17";
            bgColor = "rgba(247, 229, 164, 0.50)";
            break;
        case ClaimStatus.RECEIVED:
            textColor = "#00563E";
            bgColor = "rgba(204, 250, 233, 0.50)";
            break;
        case ClaimStatus.PATIENT_PAYMENT_COLLECTION:
            textColor = "#00563E";
            bgColor = "rgba(204, 250, 233, 0.50)";
            break;
        case ClaimStatus.CLOSED:
            textColor = "#0B132B";
            bgColor = "rgba(229, 229, 229, 0.50)";
            break;
        case ClaimStatus.SUBMITTED:
            textColor = "#00563E";
            bgColor = "rgba(204, 250, 233, 0.50)";
            break;
        case ClaimStatus.APPEALED:
            textColor = "#165574";
            bgColor = "rgba(172, 222, 250, 0.50)";
            break;
        case ClaimStatus.REJECTED:
            textColor = "#981F41";
            bgColor = "rgba(251, 199, 198, 0.50)";
            break;
        case ClaimStatus.READY_FOR_SUBMISSION:
            textColor = "#634D17";
            bgColor = "rgba(247, 229, 164, 0.50)";
            break;
        case ClaimStatus.REVERSED:
            textColor = "#981F41";
            bgColor = "rgba(251, 199, 198, 0.50)";
            break;

        case ClaimStatus.PENDING_RECOUPMENT:
            textColor = "#634D17";
            bgColor = "rgba(247, 229, 164, 0.50)";
            break;

        default:
            textColor = "#212121";
            bgColor = "rgba(245, 245, 245, 0.50)";
    }

    return (
        <Tag
            title={`${text ? `${text} ` : ``}${removeEnumUnderscore(status)}`}
            textColor={textColor}
            bgColor={bgColor}
            className="capitalize font-normal px-8 py-4 h-24 rounded-r4 text-nowrap whitespace-nowrap"
        />
    );
};

export const calculateClaimAge = (time: string) => {
    const dateSubmitted = new Date(time);
    const currentTime = new Date();
    const timeDifference = currentTime.getTime() - dateSubmitted.getTime();

    // Convert milliseconds to seconds
    const secondsDifference = Math.floor(timeDifference / 1000);

    if (secondsDifference < 60) {
        return `${secondsDifference} seconds`;
    }
    if (secondsDifference < 3600) {
        const minutes = Math.floor(secondsDifference / 60);
        return `${minutes} minute${minutes > 1 ? "s" : ""}`;
    }
    if (secondsDifference < 86400) {
        const hours = Math.floor(secondsDifference / 3600);
        return `${hours} hour${hours > 1 ? "s" : ""}`;
    }
    const days = Math.floor(secondsDifference / 86400);
    return `${days} day${days > 1 ? "s" : ""}`;
};

/**
 * Handles the extraction of the separate parts of a phone number, i.e country code, area code & number. Returns an array of all three if passed a valid number string.
 * @param phoneNumberString
 * @returns [countryCode, areaCode, phoneNumber]
 */
export const getPhoneNumberParts = (phoneNumberString: string) => {
    const cleaned = `${phoneNumberString}`.replace(/\D/g, "");
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return [match[1], match[2], `${match[3]}-${match[4]}`];
    }
    return [];
};

export const getClaimsListNavigateUrl = () => {
    const claimsListFiltersFromLS = localStorage.getItem("claimslist_filters");

    if (claimsListFiltersFromLS) {
        return `/claims?${claimsListFiltersFromLS}`;
    }
    return `/claims`;
};

// function to return styles for claim dropdown based on status
export const getClaimStatusTagStylesForDropdown = (status: ClaimStatus) => {
    let className: string;
    let strokeColor: string;

    switch (status) {
        case ClaimStatus.ACKNOWLEDGED:
            className = "text-[#00563E] bg-[#ccfae97f]";
            strokeColor = "#00563E";
            break;
        case ClaimStatus.DENIED:
            className = "text-[#981F41] bg-[#fbc7c67f]";
            strokeColor = "#981F41";
            break;
        case ClaimStatus.PAID:
            className = "text-[#00563E] bg-[#ccfae97f]";
            strokeColor = "#00563E";
            break;
        case ClaimStatus.PENDING:
            className = "text-[#634D17] bg-[#f7e5a47f]";
            strokeColor = "#634D17";
            break;
        case ClaimStatus.RECEIVED:
            className = "text-[#00563E] bg-[#ccfae97f]";
            strokeColor = "#00563E";
            break;
        case ClaimStatus.PATIENT_PAYMENT_COLLECTION:
            className = "text-[#00563E] bg-[#ccfae97f]";
            strokeColor = "#00563E";
            break;
        case ClaimStatus.CLOSED:
            className = "text-black bg-[#e5e5e57f]";
            strokeColor = "#0B132B";
            break;
        case ClaimStatus.SUBMITTED:
            className = "text-[#00563E] bg-[#ccfae97f]";
            strokeColor = "#00563E";
            break;
        case ClaimStatus.APPEALED:
            className = "text-[#165574] bg-[#acdefa7f]";
            strokeColor = "#165574";
            break;
        case ClaimStatus.REJECTED:
            className = "text-[#981F41] bg-[#fbc7c67f]";
            strokeColor = "#981F41";
            break;
        case ClaimStatus.READY_FOR_SUBMISSION:
            className = "text-[#634D17] bg-[#f7e5a47f]";
            strokeColor = "#634D17";
            break;
        case ClaimStatus.REVERSED:
            className = "text-[#981F41] bg-[#fbc7c67f]";
            strokeColor = "#981F41";
            break;
        case ClaimStatus.PENDING_RECOUPMENT:
            className = "text-[#634D17] bg-[#f7e5a47f]";
            strokeColor = "#634D17";
            break;
        default:
            className = "text-[#212121] bg-[#F5F5F5]";
            strokeColor = "#212121";
    }

    return {
        className,
        strokeColor,
    };
};

export const getClaimActivityToDisplay = (
    claimActivities: IClaimActivity[] | null,
    activityType: ClaimActivityType,
    userId: string
) => {
    let claimActivitiesToCheck: IClaimActivity[] | null = null;
    let claimActivityForUser: IClaimActivity | null = null;

    if (!claimActivities || (claimActivities && claimActivities.length === 0)) {
        claimActivitiesToCheck = null;
    }

    claimActivitiesToCheck =
        claimActivities?.filter(
            (claimAct) => claimAct.activity_type === activityType
        ) ?? null;

    claimActivityForUser =
        claimActivitiesToCheck?.find(
            (claimAct) =>
                claimAct?.user?.id === userId &&
                claimAct.activity_type === activityType
        ) ?? null;

    const isClaimAssignedToMe = Boolean(claimActivityForUser);

    const usersClaimActivityIsRelatedToFunction = () => {
        if (claimActivitiesToCheck && claimActivitiesToCheck.length > 0) {
            if (claimActivitiesToCheck.length > 1) {
                return (
                    <Tooltip
                        content={`Assigned to ${
                            claimActivityForUser ? '"You"' : ""
                        } - ${claimActivitiesToCheck
                            .filter((claimAct) => claimAct.user.id !== userId)
                            .map(
                                (claimAct) =>
                                    `"${displayNameInRightFormat({
                                        firstName: claimAct.user.first_name,
                                        lastName: claimAct.user.last_name,
                                    })}"`
                            )
                            .join("- ")}`}
                    >
                        <Button size="auto" variant="normal">
                            <Badge
                                bgColor="rgba(172, 222, 250, 0.50)"
                                textColor="#165574"
                                content={
                                    <span className="flex items-center gap-x-8">
                                        <svg
                                            viewBox="0 0 24 24"
                                            width="24"
                                            height="24"
                                            stroke="#165574"
                                            strokeWidth="2"
                                            fill="none"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="size-[12px]"
                                        >
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                            <circle cx="12" cy="7" r="4" />
                                        </svg>
                                        {`${
                                            isClaimAssignedToMe
                                                ? `You and `
                                                : ""
                                        } ${
                                            isClaimAssignedToMe
                                                ? claimActivitiesToCheck.length -
                                                  1
                                                : claimActivitiesToCheck.length
                                        } user${
                                            isClaimAssignedToMe &&
                                            claimActivitiesToCheck.length -
                                                1 ===
                                                1
                                                ? ""
                                                : "s"
                                        }`}
                                    </span>
                                }
                            />
                        </Button>
                    </Tooltip>
                );
            }

            if (claimActivitiesToCheck.length === 1 && !isClaimAssignedToMe) {
                return (
                    <Badge
                        bgColor="rgba(172, 222, 250, 0.50)"
                        textColor="#165574"
                        content={
                            <span className="flex items-center gap-x-8">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    stroke="#165574"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="size-[12px]"
                                >
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                                {displayNameInRightFormat({
                                    firstName:
                                        claimActivitiesToCheck[0].user
                                            .first_name,
                                    lastName:
                                        claimActivitiesToCheck[0].user
                                            .last_name,
                                })}
                            </span>
                        }
                    />
                );
            }

            if (claimActivitiesToCheck.length === 1 && isClaimAssignedToMe) {
                return (
                    <Badge
                        bgColor="rgba(172, 222, 250, 0.50)"
                        textColor="#165574"
                        content={
                            <span className="flex items-center gap-x-8">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    stroke="#165574"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="size-[12px]"
                                >
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                                You
                            </span>
                        }
                    />
                );
            }
        }

        return (
            <Badge
                bgColor="rgb(235, 235, 235)"
                textColor="#212121"
                content="Unassigned"
            />
        );
    };

    return {
        claimActivityForUser,
        isClaimAssignedToMe,
        usersNameToDisplay: usersClaimActivityIsRelatedToFunction(),
    };
};
